import classNames from "classnames";
import React, { ForwardRefExoticComponent, RefAttributes, useContext, useMemo } from "react";
import { DefaultPropsContext } from "../../contexts/default-props-context-provider";
import { ProgressIndicatorCircular } from "../progress-indicator";
import { Ripple } from "../ripple/ripple";
import { CSS_CLASSES } from "./button-css-classes";
import { ButtonProps } from "./button.types";
import { BUTTON_DEFAULT_COLOR, BUTTON_DEFAULT_SHAPE, BUTTON_DEFAULT_SIZE, BUTTON_DEFAULT_VARIANT } from "./constants";

/**
 * The `Button` component enables users to trigger an action on the user interface.
 * They are typically found inside forms, dialogs, panels or pages.
 */
export const Button: ForwardRefExoticComponent<ButtonProps & RefAttributes<HTMLButtonElement | HTMLAnchorElement>> =
  React.memo(
    React.forwardRef((props, ref) => {
      const defaultProps = useContext(DefaultPropsContext);
      const {
        color: defaultColor = BUTTON_DEFAULT_COLOR,
        shape: defaultShape = BUTTON_DEFAULT_SHAPE,
        size: defaultSize = BUTTON_DEFAULT_SIZE,
        variant: defaultVariant = BUTTON_DEFAULT_VARIANT,
      } = defaultProps?.buttonDefaultProps ?? {};

      const {
        label,
        labelAlign = "center",
        variant = defaultVariant,
        color = defaultColor,
        size = defaultSize,
        shape = defaultShape,
        width = "auto",
        startIcon,
        endIcon,
        mainIcon,
        href,
        external = false,
        active = false,
        disabled = false,
        loading = false,
        loadingAnimation = "swipe",
        className,
        styleOverride,
        ripple = true,
        ...restProps
      } = props;

      const maybeLoadingSwipe = useMemo(
        () => loading && loadingAnimation === "swipe" && <div className={CSS_CLASSES.BUTTON_LOADING_BAR}></div>,
        [loading, loadingAnimation]
      );

      const maybeRipple = useMemo(() => (!styleOverride && ripple ? <Ripple /> : null), [ripple, styleOverride]);

      const buttonClassName = classNames(
        CSS_CLASSES.BUTTON_ROOT,
        {
          // width
          [CSS_CLASSES.BUTTON_WIDTH_FULL]: width === "full",

          // labelAlign
          [CSS_CLASSES.BUTTON_LABEL_ALIGN_LEFT]: width === "full" && labelAlign === "left",
          [CSS_CLASSES.BUTTON_LABEL_ALIGN_CENTER]: width === "full" && labelAlign === "center",
          [CSS_CLASSES.BUTTON_LABEL_ALIGN_RIGHT]: width === "full" && labelAlign === "right",
          [CSS_CLASSES.BUTTON_LABEL_ALIGN_JUSTIFIED]: width === "full" && labelAlign === "justified",

          // variant
          [CSS_CLASSES.BUTTON_OUTLINED]: variant === "outlined",
          [CSS_CLASSES.BUTTON_GHOST]: variant === "ghost",

          // size
          [CSS_CLASSES.BUTTON_MEDIUM]: size === "medium",
          [CSS_CLASSES.BUTTON_SMALL]: size === "small",

          [CSS_CLASSES.BUTTON_OUTLINED_MEDIUM]: variant === "outlined" && size === "medium",
          [CSS_CLASSES.BUTTON_GHOST_MEDIUM]: variant === "ghost" && size === "medium",
          [CSS_CLASSES.BUTTON_OUTLINED_SMALL]: variant === "outlined" && size === "small",
          [CSS_CLASSES.BUTTON_GHOST_SMALL]: variant === "ghost" && size === "small",

          // styleOverride
          [CSS_CLASSES.BUTTON_FILLED_LARGE_FORCED_NORMAL]:
            variant === "filled" && size === "large" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_FILLED_LARGE_FORCED_HOVER]:
            variant === "filled" && size === "large" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_FILLED_LARGE_FORCED_FOCUS]:
            variant === "filled" && size === "large" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_FILLED_LARGE_FORCED_ACTIVE]:
            variant === "filled" && size === "large" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_FILLED_MEDIUM_FORCED_NORMAL]:
            variant === "filled" && size === "medium" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_FILLED_MEDIUM_FORCED_HOVER]:
            variant === "filled" && size === "medium" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_FILLED_MEDIUM_FORCED_FOCUS]:
            variant === "filled" && size === "medium" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_FILLED_MEDIUM_FORCED_ACTIVE]:
            variant === "filled" && size === "medium" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_FILLED_SMALL_FORCED_NORMAL]:
            variant === "filled" && size === "small" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_FILLED_SMALL_FORCED_HOVER]:
            variant === "filled" && size === "small" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_FILLED_SMALL_FORCED_FOCUS]:
            variant === "filled" && size === "small" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_FILLED_SMALL_FORCED_ACTIVE]:
            variant === "filled" && size === "small" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_OUTLINED_LARGE_FORCED_NORMAL]:
            variant === "outlined" && size === "large" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_OUTLINED_LARGE_FORCED_HOVER]:
            variant === "outlined" && size === "large" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_OUTLINED_LARGE_FORCED_FOCUS]:
            variant === "outlined" && size === "large" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_OUTLINED_LARGE_FORCED_ACTIVE]:
            variant === "outlined" && size === "large" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_OUTLINED_MEDIUM_FORCED_NORMAL]:
            variant === "outlined" && size === "medium" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_OUTLINED_MEDIUM_FORCED_HOVER]:
            variant === "outlined" && size === "medium" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_OUTLINED_MEDIUM_FORCED_FOCUS]:
            variant === "outlined" && size === "medium" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_OUTLINED_MEDIUM_FORCED_ACTIVE]:
            variant === "outlined" && size === "medium" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_OUTLINED_SMALL_FORCED_NORMAL]:
            variant === "outlined" && size === "small" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_OUTLINED_SMALL_FORCED_HOVER]:
            variant === "outlined" && size === "small" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_OUTLINED_SMALL_FORCED_FOCUS]:
            variant === "outlined" && size === "small" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_OUTLINED_SMALL_FORCED_ACTIVE]:
            variant === "outlined" && size === "small" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_GHOST_LARGE_FORCED_NORMAL]:
            variant === "ghost" && size === "large" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_GHOST_LARGE_FORCED_HOVER]:
            variant === "ghost" && size === "large" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_GHOST_LARGE_FORCED_FOCUS]:
            variant === "ghost" && size === "large" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_GHOST_LARGE_FORCED_ACTIVE]:
            variant === "ghost" && size === "large" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_GHOST_MEDIUM_FORCED_NORMAL]:
            variant === "ghost" && size === "medium" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_GHOST_MEDIUM_FORCED_HOVER]:
            variant === "ghost" && size === "medium" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_GHOST_MEDIUM_FORCED_FOCUS]:
            variant === "ghost" && size === "medium" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_GHOST_MEDIUM_FORCED_ACTIVE]:
            variant === "ghost" && size === "medium" && styleOverride === "active",
          [CSS_CLASSES.BUTTON_GHOST_SMALL_FORCED_NORMAL]:
            variant === "ghost" && size === "small" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_GHOST_SMALL_FORCED_HOVER]:
            variant === "ghost" && size === "small" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_GHOST_SMALL_FORCED_FOCUS]:
            variant === "ghost" && size === "small" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_GHOST_SMALL_FORCED_ACTIVE]:
            variant === "ghost" && size === "small" && styleOverride === "active",

          // color and styleOverride
          [CSS_CLASSES.BUTTON_INTERACTIVE]: color === "interactive" && !styleOverride,
          [CSS_CLASSES.BUTTON_INTERACTIVE_NORMAL]: color === "interactive" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_INTERACTIVE_HOVER]: color === "interactive" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_INTERACTIVE_FOCUS]: color === "interactive" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_INTERACTIVE_ACTIVE]: color === "interactive" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_PRIMARY]: color === "primary" && !styleOverride,
          [CSS_CLASSES.BUTTON_PRIMARY_NORMAL]: color === "primary" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_PRIMARY_HOVER]: color === "primary" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_PRIMARY_FOCUS]: color === "primary" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_PRIMARY_ACTIVE]: color === "primary" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_SECONDARY]: color === "secondary" && !styleOverride,
          [CSS_CLASSES.BUTTON_SECONDARY_NORMAL]: color === "secondary" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_SECONDARY_HOVER]: color === "secondary" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_SECONDARY_FOCUS]: color === "secondary" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_SECONDARY_ACTIVE]: color === "secondary" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_ACCENT]: color === "accent" && !styleOverride,
          [CSS_CLASSES.BUTTON_ACCENT_NORMAL]: color === "accent" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_ACCENT_HOVER]: color === "accent" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_ACCENT_FOCUS]: color === "accent" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_ACCENT_ACTIVE]: color === "accent" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_GRAY]: color === "gray" && !styleOverride,
          [CSS_CLASSES.BUTTON_GRAY_NORMAL]: color === "gray" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_GRAY_HOVER]: color === "gray" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_GRAY_FOCUS]: color === "gray" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_GRAY_ACTIVE]: color === "gray" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_ERROR]: color === "error" && !styleOverride,
          [CSS_CLASSES.BUTTON_ERROR_NORMAL]: color === "error" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_ERROR_HOVER]: color === "error" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_ERROR_FOCUS]: color === "error" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_ERROR_ACTIVE]: color === "error" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_WARNING]: color === "warning" && !styleOverride,
          [CSS_CLASSES.BUTTON_WARNING_NORMAL]: color === "warning" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_WARNING_HOVER]: color === "warning" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_WARNING_FOCUS]: color === "warning" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_WARNING_ACTIVE]: color === "warning" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_SUCCESS]: color === "success" && !styleOverride,
          [CSS_CLASSES.BUTTON_SUCCESS_NORMAL]: color === "success" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_SUCCESS_HOVER]: color === "success" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_SUCCESS_FOCUS]: color === "success" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_SUCCESS_ACTIVE]: color === "success" && (active || styleOverride === "active"),

          [CSS_CLASSES.BUTTON_WHITE]: color === "white" && !styleOverride,
          [CSS_CLASSES.BUTTON_WHITE_NORMAL]: color === "white" && styleOverride === "normal",
          [CSS_CLASSES.BUTTON_WHITE_HOVER]: color === "white" && styleOverride === "hover",
          [CSS_CLASSES.BUTTON_WHITE_FOCUS]: color === "white" && styleOverride === "focus",
          [CSS_CLASSES.BUTTON_WHITE_ACTIVE]: color === "white" && (active || styleOverride === "active"),

          // shape
          [CSS_CLASSES.BUTTON_SQUARE]: shape === "square",
          [CSS_CLASSES.BUTTON_PILL]: shape === "pill",

          // loading
          [CSS_CLASSES.BUTTON_LOADING]: loading,
          [CSS_CLASSES.BUTTON_LOADING_SPINNER_ONLY]: !href && loading && loadingAnimation === "spinner-only",

          // disabled
          [CSS_CLASSES.BUTTON_DISABLED]: disabled || styleOverride === "disabled",
        },
        className
      );

      restProps.title = restProps.title || (mainIcon ? label : undefined);
      restProps.type = href ? undefined : restProps.type || "button";

      const loadingIndicator = (
        <ProgressIndicatorCircular
          size="small"
          color={
            variant === "filled" && color !== "white"
              ? "white"
              : variant === "filled" && color === "white"
              ? "primary"
              : color
          }
          className={!mainIcon && loadingAnimation === "spinner-only" ? "absolute" : undefined}
        />
      );

      const labelText = <span className={CSS_CLASSES.BUTTON_LABEL_TEXT}>{label}</span>;

      return href !== undefined ? (
        <a
          ref={ref as any}
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          className={buttonClassName}
          {...restProps}
        >
          {startIcon}
          {labelText}
          {endIcon}
        </a>
      ) : (
        <button
          ref={ref as any}
          disabled={disabled || loading || styleOverride === "disabled"}
          className={buttonClassName}
          {...restProps}
        >
          {maybeLoadingSwipe}
          {maybeRipple}
          {mainIcon ? (
            loading && loadingAnimation !== "swipe" ? (
              loadingIndicator
            ) : (
              mainIcon
            )
          ) : (
            <>
              {loading && loadingAnimation === "spinner-only" && loadingIndicator}
              {loading && loadingAnimation === "spinner" && !endIcon ? loadingIndicator : startIcon}
              {labelText}
              {loading && loadingAnimation === "spinner" && !startIcon && endIcon ? loadingIndicator : endIcon}
            </>
          )}
        </button>
      );
    })
  );

Button.displayName = "Button";
